// src/components/dashboard/EventDetails/EventList.jsx
// 事件列表组件 - 显示最近的洪水事件

import React, { useState } from 'react';
import { Clock, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { EventCardCompact } from './EventCard';
import Button from '../../ui/Button/Button';
import styles from './EventDetails.module.css';

const EventList = ({ 
  allSensors = [], 
  selectedSensor, 
  onSensorSelect,
  maxItems = 10,
  showFilters = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [sortBy, setSortBy] = useState('time'); // time, severity, location
  const [filterSeverity, setFilterSeverity] = useState('all');

  // 收集所有事件并排序
  const getAllEvents = () => {
    const events = [];
    
    allSensors.forEach(sensor => {
      if (sensor.events && sensor.events.length > 0) {
        sensor.events.forEach((event, index) => {
          events.push({
            ...event,
            sensor,
            id: `${sensor.id}-${index}`,
            sensorId: sensor.id
          });
        });
      }
    });

    // 应用过滤器
    let filteredEvents = events;
    if (filterSeverity !== 'all') {
      filteredEvents = events.filter(event => event.severity === filterSeverity);
    }

    // 🔧 修复：排序 - 添加大括号创建块级作用域
    filteredEvents.sort((a, b) => {
      switch (sortBy) {
        case 'severity': {
          const severityOrder = { severe: 3, moderate: 2, mild: 1 };
          return (severityOrder[b.severity] || 0) - (severityOrder[a.severity] || 0);
        }
        case 'location':
          return a.sensor.location.localeCompare(b.sensor.location);
        case 'time':
        default:
          return new Date(b.timestamp) - new Date(a.timestamp);
      }
    });

    return filteredEvents;
  };

  const allEvents = getAllEvents();
  const displayEvents = isExpanded ? allEvents : allEvents.slice(0, maxItems);

  if (allEvents.length === 0) {
    return (
      <div className={styles.eventListContainer}>
        <div className={styles.eventListHeader}>
          <h3 className={styles.eventListTitle}>
            <Clock className={styles.titleIcon} />
            Recent Events
          </h3>
        </div>
        <div className={styles.emptyEventList}>
          <p>No recent flood events found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.eventListContainer}>
      {/* 列表头部 */}
      <div className={styles.eventListHeader}>
        <h3 className={styles.eventListTitle}>
          <Clock className={styles.titleIcon} />
          Recent Events
          <span className={styles.eventCount}>({allEvents.length})</span>
        </h3>
        
        {/* 过滤和排序控件 */}
        {showFilters && (
          <div className={styles.eventListControls}>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className={styles.controlSelect}
            >
              <option value="time">Sort by Time</option>
              <option value="severity">Sort by Severity</option>
              <option value="location">Sort by Location</option>
            </select>
            
            <select 
              value={filterSeverity} 
              onChange={(e) => setFilterSeverity(e.target.value)}
              className={styles.controlSelect}
            >
              <option value="all">All Severities</option>
              <option value="severe">Severe</option>
              <option value="moderate">Moderate</option>
              <option value="mild">Mild</option>
            </select>
          </div>
        )}
      </div>

      {/* 事件列表 */}
      <div className={styles.eventListContent}>
        <div className={styles.eventListScrollArea}>
          {displayEvents.map((event) => (
            <EventCardCompact
              key={event.id}
              event={event}
              sensor={event.sensor}
              isSelected={selectedSensor?.id === event.sensorId}
              onClick={onSensorSelect}
            />
          ))}
        </div>
        
        {/* 展开/收起按钮 */}
        {allEvents.length > maxItems && (
          <div className={styles.eventListFooter}>
            <Button
              variant="ghost"
              size="sm"
              icon={isExpanded ? <ChevronUp /> : <ChevronDown />}
              onClick={() => setIsExpanded(!isExpanded)}
              className={styles.expandButton}
            >
              {isExpanded 
                ? `Show Less` 
                : `Show ${allEvents.length - maxItems} More`
              }
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventList;